import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import {
  User,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  FileText,
  Activity,
  CreditCard,
  MessageSquare,
  Save,
  X,
  History,
  ArrowRightLeft,
  Plus,
  Minus,
  Edit,
  UserPlus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface CreateCaseDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCaseCreated?: () => void;
}

interface NewCaseData {
  customerName: string;
  customerId: string;
  customerEmail: string;
  customerPhone: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Active' | 'On Hold' | 'Closed';
  riskLevel: 'High' | 'Medium' | 'Low';
  strategyId: string;
  notes: string;
  nextAction: string;
  nextActionDate: string;
}

interface Transaction {
  id: string;
  invoiceNumber: string;
  invoiceDate: Date;
  dueDate: Date;
  amount: number;
  balance: number;
  status: 'Outstanding' | 'Overdue';
  daysOverdue: number;
}

export function CreateCaseDrawer({ open, onOpenChange, onCaseCreated }: CreateCaseDrawerProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [newNoteContent, setNewNoteContent] = useState("");
  
  // Available collection strategies
  const availableStrategies = [
    { id: "strategy-1", name: "Standard Collection", type: "Standard" },
    { id: "strategy-2", name: "Intensive Follow-up", type: "Intensive" },
    { id: "strategy-3", name: "VIP Treatment", type: "VIP" },
    { id: "strategy-4", name: "High Risk Protocol", type: "High Risk" },
  ];

  // Form data state
  const [formData, setFormData] = useState<NewCaseData>({
    customerName: "",
    customerId: "",
    customerEmail: "",
    customerPhone: "",
    priority: "Medium",
    status: "Active",
    riskLevel: "Medium",
    strategyId: "strategy-1", // Default to Standard Collection
    notes: "",
    nextAction: "Follow-up call",
    nextActionDate: format(new Date(), "yyyy-MM-dd"),
  });

  // Mock available transactions for selection
  const [availableTransactions] = useState<Transaction[]>([
    {
      id: "1",
      invoiceNumber: "INV-2024-001",
      invoiceDate: new Date(2024, 0, 15),
      dueDate: new Date(2024, 1, 15),
      amount: 6000,
      balance: 6000,
      status: "Overdue",
      daysOverdue: 45,
    },
    {
      id: "2",
      invoiceNumber: "INV-2024-002",
      invoiceDate: new Date(2024, 1, 1),
      dueDate: new Date(2024, 2, 1),
      amount: 5250,
      balance: 5250,
      status: "Overdue",
      daysOverdue: 30,
    },
    {
      id: "3",
      invoiceNumber: "INV-2024-003",
      invoiceDate: new Date(2024, 2, 10),
      dueDate: new Date(2024, 3, 10),
      amount: 3750,
      balance: 3750,
      status: "Outstanding",
      daysOverdue: 0,
    },
  ]);

  // Reset form when dialog opens/closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset form
      setFormData({
        customerName: "",
        customerId: "",
        customerEmail: "",
        customerPhone: "",
        priority: "Medium",
        status: "Active",
        riskLevel: "Medium",
        strategyId: "strategy-1", // Reset to default
        notes: "",
        nextAction: "Follow-up call",
        nextActionDate: format(new Date(), "yyyy-MM-dd"),
      });
      setSelectedTransactions(new Set());
      setNewNoteContent("");
      setActiveTab("overview");
    }
    onOpenChange(open);
  };

  const handleToggleTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const validateForm = (): boolean => {
    // Required fields validation
    if (!formData.customerName.trim()) {
      toast.error("Customer name is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.customerId.trim()) {
      toast.error("Customer ID is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.customerEmail.trim()) {
      toast.error("Customer email is required");
      setActiveTab("overview");
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customerEmail)) {
      toast.error("Please enter a valid email address");
      setActiveTab("overview");
      return false;
    }

    if (!formData.customerPhone.trim()) {
      toast.error("Customer phone is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextAction.trim()) {
      toast.error("Next action is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextActionDate) {
      toast.error("Next action date is required");
      setActiveTab("overview");
      return false;
    }

    // At least one transaction must be selected
    if (selectedTransactions.size === 0) {
      toast.error("Please select at least one transaction");
      setActiveTab("transactions");
      return false;
    }

    return true;
  };

  const handleSaveCase = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Calculate total amount from selected transactions
      const totalAmount = availableTransactions
        .filter(t => selectedTransactions.has(t.id))
        .reduce((sum, t) => sum + t.balance, 0);

      // Here you would make the actual API call to create the case
      console.log("Creating new case:", {
        ...formData,
        selectedTransactions: Array.from(selectedTransactions),
        totalAmount,
      });

      toast.success("Collection case created successfully");
      
      // Call the callback to refresh the queue
      if (onCaseCreated) {
        onCaseCreated();
      }

      // Close the drawer
      handleOpenChange(false);
    } catch (error) {
      toast.error("Failed to create case. Please try again.");
      console.error("Error creating case:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "On Hold":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "Closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "";
    }
  };

  const calculateTotalAmount = () => {
    return availableTransactions
      .filter(t => selectedTransactions.has(t.id))
      .reduce((sum, t) => sum + t.balance, 0);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl overflow-y-auto px-6">
        <SheetHeader>
          <div className="flex items-start justify-between pr-8">
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle className="text-2xl">Create New Case</SheetTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enter customer and case information to create a new collection case
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenChange(false)}
                    disabled={isSaving}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveCase}
                    disabled={isSaving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Creating..." : "Create Case"}
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex gap-2 ml-4">
              <Badge variant={getPriorityBadgeVariant(formData.priority)}>
                {formData.priority} Priority
              </Badge>
              <Badge className={getStatusBadgeColor(formData.status)}>
                {formData.status}
              </Badge>
            </div>
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="notes">Notes History</TabsTrigger>
            <TabsTrigger value="communications">Communications</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[calc(90vh-240px)] mt-4">
            <TabsContent value="overview" className="space-y-4">
              {/* Customer Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Customer Information
                  </CardTitle>
                  <CardDescription>Enter the customer details for this collection case</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="customerName" className="text-sm font-medium">
                      Customer Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="customerName"
                      value={formData.customerName}
                      onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                      placeholder="Enter customer name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerId" className="text-sm font-medium">
                      Customer ID <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="customerId"
                      value={formData.customerId}
                      onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                      placeholder="Enter customer ID"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                      placeholder="customer@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      Phone <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.customerPhone}
                      onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Case Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Case Details
                  </CardTitle>
                  <CardDescription>Configure the case settings and priority</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="priority" className="text-sm font-medium">
                      Priority <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                        setFormData({ ...formData, priority: value })
                      }
                    >
                      <SelectTrigger id="priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status" className="text-sm font-medium">
                      Status <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value: 'Active' | 'On Hold' | 'Closed') => 
                        setFormData({ ...formData, status: value })
                      }
                    >
                      <SelectTrigger id="status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="On Hold">On Hold</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="riskLevel" className="text-sm font-medium flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />
                      Risk Level <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.riskLevel}
                      onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                        setFormData({ ...formData, riskLevel: value })
                      }
                    >
                      <SelectTrigger id="riskLevel">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="strategyId" className="text-sm font-medium flex items-center gap-1">
                      <Activity className="h-4 w-4" />
                      Collection Strategy <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.strategyId}
                      onValueChange={(value) => setFormData({ ...formData, strategyId: value })}
                    >
                      <SelectTrigger id="strategyId">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableStrategies.map((strategy) => (
                          <SelectItem key={strategy.id} value={strategy.id}>
                            {strategy.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {availableStrategies.find(s => s.id === formData.strategyId)?.type}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-1">
                      <DollarSign className="h-4 w-4" />
                      Outstanding Amount
                    </Label>
                    <div className="flex items-center h-10 px-3 py-2 border rounded-md bg-muted">
                      <p className="text-base font-semibold text-red-600 dark:text-red-400">
                        ${calculateTotalAmount().toLocaleString()}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Calculated from selected transactions
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Next Action */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Next Action
                  </CardTitle>
                  <CardDescription>Schedule the next follow-up action</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="nextAction" className="text-sm font-medium">
                      Action Type <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.nextAction}
                      onValueChange={(value) => setFormData({ ...formData, nextAction: value })}
                    >
                      <SelectTrigger id="nextAction">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Follow-up call">Follow-up call</SelectItem>
                        <SelectItem value="Send email">Send email</SelectItem>
                        <SelectItem value="Send letter">Send letter</SelectItem>
                        <SelectItem value="Payment reminder">Payment reminder</SelectItem>
                        <SelectItem value="Escalate to manager">Escalate to manager</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nextActionDate" className="text-sm font-medium">
                      Due Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="nextActionDate"
                      type="date"
                      value={formData.nextActionDate}
                      onChange={(e) => setFormData({ ...formData, nextActionDate: e.target.value })}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Initial Notes
                  </CardTitle>
                  <CardDescription>Add any relevant notes about this case</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Add notes about this case..."
                    rows={4}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="transactions" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Select Transactions</CardTitle>
                      <CardDescription>
                        Choose the invoices to include in this collection case ({selectedTransactions.size} selected)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Invoice Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Days Overdue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {availableTransactions.map((transaction) => (
                        <TableRow 
                          key={transaction.id}
                          className={cn(
                            "cursor-pointer hover:bg-muted/50",
                            selectedTransactions.has(transaction.id) && "bg-muted/30"
                          )}
                          onClick={() => handleToggleTransaction(transaction.id)}
                        >
                          <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedTransactions.has(transaction.id)}
                              onCheckedChange={() => handleToggleTransaction(transaction.id)}
                              aria-label={`Select ${transaction.invoiceNumber}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {transaction.invoiceNumber}
                          </TableCell>
                          <TableCell>
                            {format(transaction.invoiceDate, "MMM d, yyyy")}
                          </TableCell>
                          <TableCell>
                            {format(transaction.dueDate, "MMM d, yyyy")}
                          </TableCell>
                          <TableCell className="text-right">
                            ${transaction.amount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            ${transaction.balance.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                transaction.status === "Overdue"
                                  ? "destructive"
                                  : "secondary"
                              }
                            >
                              {transaction.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {transaction.daysOverdue > 0 ? (
                              <span className="text-red-600 dark:text-red-400 font-medium">
                                {transaction.daysOverdue}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Separator className="my-4" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Outstanding:</span>
                    <span className="text-xl font-bold text-red-600 dark:text-red-400">
                      ${calculateTotalAmount().toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Activity Timeline</CardTitle>
                  <CardDescription>
                    Activity history will appear here after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No activity yet</p>
                    <p className="text-xs mt-1">Activities will be logged once the case is created</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notes" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Notes History</CardTitle>
                  <CardDescription>
                    Notes will be available after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No notes yet</p>
                    <p className="text-xs mt-1">You can add notes after creating the case</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="communications" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Communication History</CardTitle>
                  <CardDescription>
                    Communication history will appear here after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No communications yet</p>
                    <p className="text-xs mt-1">Communications will be tracked once the case is created</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="audit" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>
                    Audit trail will be available after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No audit entries yet</p>
                    <p className="text-xs mt-1">All case modifications will be logged after creation</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
